import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useContainerQuery } from "@/hooks/use-container-query"
import { useMediaQuery } from "@/hooks/use-media-query"
import { DayObject, DayRange } from "@/types/date"
import { DayFlag, DayPicker, SelectionState, UI } from "@daypicker/react"
import { cn } from "cn"

import {
  convertFromInternalDate,
  convertToInternalDate,
  convertToInternalRange,
  wrapCallback,
} from "@/lib/date-integration/daypicker"
import {
  addDays,
  addMonths,
  beforeDayObject,
  browserToday,
  sameDay,
  startOfMonth,
} from "@/lib/date-utils"
import { Locale } from "@/lib/locale-hooks"
import { assert } from "@/lib/utils/assertions"
import { compact } from "@/lib/utils/collections"
import IconChevronLeft from "@/components/icons/chevron-left"
import { Button } from "./ui/button"

const DEFAULT_PLANNING_MONTHS_AHEAD = 120
const MONTHS_PER_PAGE = 24
const SCROLL_ANCHOR_TOP_OFFSET_PX = 32

function scrollElementToTopWithOffset(
  element: HTMLElement,
  scrollParent: HTMLElement,
  offsetPx: number
) {
  const top =
    element.getBoundingClientRect().top -
    scrollParent.getBoundingClientRect().top +
    scrollParent.scrollTop
  scrollParent.scrollTop = Math.max(0, top - offsetPx)
}

function scrollToFrozenAnchorMonth(
  root: HTMLElement,
  scrollParent: HTMLElement,
  frozenAnchorMonth: DayObject,
  scrollWindowStartMonth: DayObject
) {
  if (sameMonth(frozenAnchorMonth, scrollWindowStartMonth)) {
    scrollParent.scrollTop = 0
    return true
  }

  const anchor = root.querySelector<HTMLElement>('[data-scroll-anchor="true"]')
  if (!anchor) return false

  scrollElementToTopWithOffset(
    anchor,
    scrollParent,
    SCROLL_ANCHOR_TOP_OFFSET_PX
  )
  return true
}

// next/previous month buttons only have aria-disabled, not disabled
const buttonClasses =
  "size-12 flex items-center justify-center rounded-full border border-transparent enabled:hover:bg-foreground/5 disabled:text-gray-8 aria-disabled:text-gray-8 aria-disabled:hover:bg-transparent aria-disabled:cursor-default"

function countMonthsInRange(start: DayObject, end: DayObject): number {
  return (end.year - start.year) * 12 + (end.month - start.month) + 1
}

function getScrollParent(element: HTMLElement | null): HTMLElement | null {
  let parent = element?.parentElement ?? null
  while (parent) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent
    }
    parent = parent.parentElement
  }
  return null
}

function sameMonth(a: DayObject, b: DayObject): boolean {
  return a.year === b.year && a.month === b.month
}

function loadedMonthsForAnchor(
  rangeStart: DayObject,
  anchorMonth: DayObject,
  totalMonths: number
): number {
  const monthIndex = Math.max(
    1,
    countMonthsInRange(rangeStart, startOfMonth(anchorMonth))
  )
  const pages = Math.ceil(monthIndex / MONTHS_PER_PAGE)
  return Math.max(1, Math.min(totalMonths, pages * MONTHS_PER_PAGE))
}

export function DatePickerWeekdayHeaderRow({
  className,
}: {
  className?: string
}) {
  const formatWeekdayAbbrev = Locale.useFormatWeekdayAbbrev()
  const weekStartsOn = Locale.useWeekStartsOn()
  // Jan 7 2024 is a Sunday; increment so the row starts on the locale's first day.
  const sunday: DayObject = { year: 2024, month: 1, day: 7 }
  const weekdays = Array.from({ length: 7 }, (_, index) => {
    const date = convertToInternalDate(
      addDays(sunday, (weekStartsOn + index) % 7)
    )
    assert(date, "expected valid weekday date")
    return formatWeekdayAbbrev(date)
  })

  return (
    <div
      className={cn(
        "no-translate mx-auto grid w-84 shrink-0 grid-cols-7 text-center text-xs font-semibold text-muted",
        className
      )}
    >
      {weekdays.map((weekday, index) => (
        <div key={index}>{weekday}</div>
      ))}
    </div>
  )
}

export function DatePicker({
  dayRange,
  defaultStartDay,
  retDayRangeCallback,
  min,
  max,
  numberOfMonths,
  mode = "range",
  duration,
  firstClickHint,
  useRootWidth,
  hasMinHeight = true,
  onScrollableMonthsChange,
  monthWindowOpen,
  disabledWeekdays,
}: {
  dayRange: DayRange
  defaultStartDay?: DayObject
  retDayRangeCallback: (dayRange: DayRange) => void
  min?: DayObject | undefined
  max?: DayObject | undefined
  numberOfMonths?: number | undefined
  mode: "single" | "range"
  duration?: number // enforce this many nights
  firstClickHint?: "from" | "to"
  useRootWidth?: boolean
  hasMinHeight?: boolean
  onScrollableMonthsChange?: (scrollable: boolean) => void
  /** When provided, the month window resets each time this flips to open. */
  monthWindowOpen?: boolean
  disabledWeekdays?: number[]
}) {
  const formatWeekdayAbbrev = Locale.useFormatWeekdayAbbrev()
  const weekStartsOn = Locale.useWeekStartsOn()
  assert(!duration || duration >= 0, "duration can't be negative")
  assert(
    mode === "range" || duration == null,
    "duration is only valid for range mode"
  )
  assert(
    mode === "range" || firstClickHint == null,
    "firstClickHint is only valid for range mode"
  )

  // If there's any ambiguity about whether the user's next click
  // on a calendar day is meant to set the "from" or "to" of a day
  // range, use this value to decide. The click handlers may flip
  // this back and forth.
  const [nextRangeValueToAdjust, setNextRangeValueToAdjust] = useState<
    "to" | "from"
  >(firstClickHint || "from")

  const handleDayClick = (day: DayObject | undefined) => {
    let from: DayObject | undefined
    let to: DayObject | undefined
    if (!day) {
      from = to = undefined
    } else if (mode === "single") {
      from = to = day
    } else if (duration) {
      // We're enforcing a strict duration and we take every
      // click as the start of the range.
      from = day
      to = from ? addDays(from, duration) : undefined
    } else if (!dayRange.from) {
      // No start date yet - set the clicked day as the start
      from = day
      to = undefined
      setNextRangeValueToAdjust("to")
    } else if (dayRange.to) {
      // Complete range exists — use nextRangeValueToAdjust (not the
      // static firstClickHint prop) so that after the first click
      // adjusts one end, the next click adjusts the other end.
      if (nextRangeValueToAdjust === "to") {
        if (beforeDayObject(day, dayRange.from)) {
          from = day
          to = undefined
          setNextRangeValueToAdjust("to")
        } else {
          from = dayRange.from
          to = day
          setNextRangeValueToAdjust("from")
        }
      } else {
        if (
          beforeDayObject(day, dayRange.from) ||
          beforeDayObject(dayRange.to, day)
        ) {
          from = day
          to = undefined
          setNextRangeValueToAdjust("to")
        } else {
          from = day
          to = dayRange.to
          setNextRangeValueToAdjust("to")
        }
      }
    } else if (beforeDayObject(day, dayRange.from)) {
      // Have start but no end, clicked before start - make this the new start
      from = day
      to = undefined
      setNextRangeValueToAdjust("to")
    } else {
      // Have start but no end, clicked at or after start - set as end
      from = dayRange.from
      to = day
      setNextRangeValueToAdjust("from")
    }

    if (to) {
      assert(from, "expected from day to exist")
    }
    if (to && from) {
      assert(!beforeDayObject(to, from), "to can't be before from")
    }

    retDayRangeCallback({ from, to })
  }

  const minDate = min && convertToInternalDate(min)
  const maxDate = max && convertToInternalDate(max)

  const rootRef = useRef<HTMLDivElement>(null)
  const rootWidthSupportsTwoMonths = useContainerQuery(rootRef, 718)
  const windowWidthSupportsTwoMonths = useMediaQuery("(min-width:752px)")
  const showTwoMonths = useRootWidth
    ? rootWidthSupportsTwoMonths
    : windowWidthSupportsTwoMonths
  const useScrollableMonths = !showTwoMonths && numberOfMonths === undefined

  const minBoundMonth = startOfMonth(min || browserToday())
  const rangeEndDay = max
    ? startOfMonth(max)
    : startOfMonth(addMonths(minBoundMonth, DEFAULT_PLANNING_MONTHS_AHEAD - 1))

  const monthWindowOpenSessionRef = useRef(0)
  const wasMonthWindowOpenRef = useRef(false)
  if (monthWindowOpen && !wasMonthWindowOpenRef.current) {
    monthWindowOpenSessionRef.current += 1
  }
  wasMonthWindowOpenRef.current = Boolean(monthWindowOpen)

  // Intentionally excludes defaultStartDay so picking dates does not
  // re-anchor or re-scroll. defaultStartDay is snapshotted when this
  // key changes (e.g. dialog open).
  const scrollSessionKey = [
    monthWindowOpen !== undefined ? monthWindowOpenSessionRef.current : 0,
    min ? `${min.year}-${min.month}` : "none",
    max ? `${max.year}-${max.month}` : "none",
    useScrollableMonths,
  ].join("|")

  const initialAnchorMonth = startOfMonth(defaultStartDay || minBoundMonth)
  const frozenAnchorRef = useRef({
    sessionKey: scrollSessionKey,
    month: initialAnchorMonth,
  })
  if (frozenAnchorRef.current.sessionKey !== scrollSessionKey) {
    frozenAnchorRef.current = {
      sessionKey: scrollSessionKey,
      month: initialAnchorMonth,
    }
  }
  const frozenAnchorMonth = frozenAnchorRef.current.month

  // When the trip anchor is before min (e.g. editing a past trip), include
  // those months in the scroll window so the calendar still renders.
  const scrollWindowStartMonth = beforeDayObject(
    frozenAnchorMonth,
    minBoundMonth
  )
    ? frozenAnchorMonth
    : minBoundMonth

  const totalMonthCount = Math.max(
    1,
    countMonthsInRange(scrollWindowStartMonth, rangeEndDay)
  )
  const initialLoadedMonthCount = loadedMonthsForAnchor(
    scrollWindowStartMonth,
    frozenAnchorMonth,
    totalMonthCount
  )
  const pickerStartMonth =
    convertToInternalDate(scrollWindowStartMonth) ?? minDate

  const [loadedMonthsState, setLoadedMonthsState] = useState({
    sessionKey: scrollSessionKey,
    count: initialLoadedMonthCount,
  })
  const loadedMonthCount =
    loadedMonthsState.sessionKey === scrollSessionKey
      ? Math.min(loadedMonthsState.count, totalMonthCount)
      : initialLoadedMonthCount
  const canLoadMoreMonths = loadedMonthCount < totalMonthCount

  const resolvedNumberOfMonths =
    numberOfMonths ??
    (showTwoMonths
      ? 2
      : useScrollableMonths
        ? Math.max(1, loadedMonthCount)
        : 1)

  useEffect(() => {
    onScrollableMonthsChange?.(useScrollableMonths)
  }, [onScrollableMonthsChange, useScrollableMonths])

  // frozenAnchorMonth and scrollWindowStartMonth are stable within a scroll
  // session (the former is frozen per session, the latter derives from it), so
  // we intentionally key this effect on the session rather than those values.
  useLayoutEffect(() => {
    if (!useScrollableMonths) return

    const root = rootRef.current
    if (!root) return

    const scrollParent = getScrollParent(root)
    if (!scrollParent) return

    let rafId = 0
    const run = () => {
      const didScroll = scrollToFrozenAnchorMonth(
        root,
        scrollParent,
        frozenAnchorMonth,
        scrollWindowStartMonth
      )
      if (!didScroll) {
        rafId = requestAnimationFrame(run)
      }
    }
    run()

    return () => cancelAnimationFrame(rafId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollSessionKey, useScrollableMonths])

  const pickerDisplayMonth =
    convertToInternalDate(frozenAnchorMonth) ||
    convertToInternalDate(min || browserToday()) ||
    minDate

  return (
    <div
      ref={rootRef}
      className={cn(
        "text-center",
        hasMinHeight && !useScrollableMonths && "min-h-96"
      )}
    >
      <DayPicker
        key={scrollSessionKey}
        {...(mode === "single"
          ? {
              mode: "single",
              selected: convertToInternalDate(dayRange.from),
              required: true,
            }
          : {
              mode: "range",
              selected: convertToInternalRange(dayRange),
            })}
        // 'selected' prop only works with onSelect handler defined
        onSelect={() => {}}
        onDayClick={wrapCallback(handleDayClick)}
        {...(useScrollableMonths
          ? {
              month: convertToInternalDate(scrollWindowStartMonth),
            }
          : {
              defaultMonth: pickerDisplayMonth,
            })}
        startMonth={pickerStartMonth}
        endMonth={maxDate}
        weekStartsOn={weekStartsOn}
        disabled={compact([
          minDate && { before: minDate },
          maxDate && { after: maxDate },
          disabledWeekdays?.length
            ? { dayOfWeek: disabledWeekdays }
            : undefined,
        ])}
        numberOfMonths={resolvedNumberOfMonths}
        hideNavigation={useScrollableMonths}
        hideWeekdays={useScrollableMonths}
        classNames={{
          [UI.Months]: cn(
            useScrollableMonths
              ? "relative mx-auto flex w-84 flex-col gap-6"
              : "relative mx-auto flex w-fit gap-12"
          ),
          [UI.MonthCaption]: cn(
            useScrollableMonths ? "pb-1.5 text-left" : "h-12"
          ),
          [UI.CaptionLabel]: cn(
            useScrollableMonths
              ? "text-base font-semibold"
              : "inline-flex h-12 items-center font-semibold"
          ),
          [UI.Nav]:
            "pointer-events-none absolute inset-x-0 flex justify-between",
          [UI.PreviousMonthButton]: `${buttonClasses} pointer-events-auto`,
          [UI.NextMonthButton]: `${buttonClasses} pointer-events-auto -scale-x-100`,
          [UI.MonthGrid]: cn(useScrollableMonths ? "mx-auto w-fit" : "w-full"),
          [UI.Weekday]: "text-muted size-12 text-center text-xs font-semibold",
          [UI.Day]: "group relative size-12 rounded-full p-0",
          [UI.DayButton]: `${buttonClasses} hover:enabled:group-[.range-middle]:border-foreground`,
          [DayFlag.outside]: "bg-transparent!",
          ...(mode === "single" ||
          !dayRange.to ||
          (dayRange.from && dayRange.to && sameDay(dayRange.from, dayRange.to))
            ? {
                [SelectionState.selected]:
                  "*:bg-foreground! *:text-background!",
              }
            : {
                [SelectionState.range_start]:
                  "rounded-l-full bg-gray-3 rounded-r-none *:bg-foreground! *:text-background!",
                [SelectionState.range_middle]:
                  "range-middle rounded-none bg-gray-3",
                [SelectionState.range_end]:
                  "rounded-r-full rounded-l-none bg-gray-3 *:bg-foreground! *:text-background!",
              }),
        }}
        formatters={{
          formatWeekdayName: formatWeekdayAbbrev,
        }}
        components={{
          Chevron() {
            return (
              <IconChevronLeft
                strokeWidth={2.5}
                className="-translate-x-px text-lg"
              />
            )
          },
          Weekday(props) {
            return (
              <th {...props} className={cn(props.className, "no-translate")} />
            )
          },
          ...(useScrollableMonths
            ? {
                MonthCaption(props) {
                  const { calendarMonth, children, ...rest } = props
                  const month = startOfMonth(
                    convertFromInternalDate(calendarMonth.date)
                  )
                  const isAnchor = sameMonth(month, frozenAnchorMonth)
                  return (
                    <div
                      {...rest}
                      data-scroll-anchor={isAnchor ? "true" : undefined}
                    >
                      {children}
                    </div>
                  )
                },
              }
            : {}),
        }}
      />
      {useScrollableMonths && canLoadMoreMonths && (
        <Button
          variant="subtle"
          size="md"
          block
          className="mt-5"
          onClick={() =>
            setLoadedMonthsState((state) => ({
              sessionKey: scrollSessionKey,
              count:
                (state.sessionKey === scrollSessionKey
                  ? state.count
                  : initialLoadedMonthCount) + MONTHS_PER_PAGE,
            }))
          }
        >
          Show more dates
        </Button>
      )}
    </div>
  )
}
